import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, TrendingUp, AlertCircle, Target, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Insight {
  id: string;
  type: "opportunity" | "warning" | "action" | "trend";
  title: string;
  description: string;
  action?: {
    label: string;
    path: string;
  };
  priority: "high" | "medium" | "low";
}

const mockInsights: Insight[] = [
  {
    id: "1",
    type: "action",
    title: "7 leads need follow-up",
    description: "You have high-value leads that haven't been contacted in over 5 days",
    action: { label: "Review Leads", path: "/leads" },
    priority: "high"
  },
  {
    id: "2",
    type: "opportunity",
    title: "Close rate trending up",
    description: "Your close rate improved by 15% this month. Consider documenting what's working.",
    priority: "medium"
  },
  {
    id: "3",
    type: "trend",
    title: "Peak response times",
    description: "Leads contacted within 2 hours convert 3x better",
    priority: "medium"
  },
  {
    id: "4",
    type: "warning",
    title: "Proposal delays",
    description: "3 proposals have been pending for over 2 weeks",
    action: { label: "View Proposals", path: "/jobs" },
    priority: "high"
  }
];

const iconMap = {
  opportunity: TrendingUp,
  warning: AlertCircle,
  action: Target,
  trend: Sparkles
};

const priorityColors = {
  high: "destructive",
  medium: "default",
  low: "secondary"
} as const;

export function AIInsightsPanel() {
  const navigate = useNavigate();

  return (
    <Card className="backdrop-blur-xl bg-gradient-to-br from-primary/10 via-white/10 to-white/5 dark:from-primary/10 dark:via-black/10 dark:to-black/5 border-white/20">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/20 backdrop-blur-sm">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <CardTitle className="text-lg font-semibold">AI Insights</CardTitle>
          </div>
          <Badge variant="secondary" className="backdrop-blur-sm">Live</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {mockInsights.map((insight) => {
          const Icon = iconMap[insight.type];
          return (
            <div 
              key={insight.id}
              className="group p-4 rounded-xl backdrop-blur-sm bg-white/5 hover:bg-white/10 dark:bg-black/5 dark:hover:bg-black/10 border border-white/10 transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-sm">{insight.title}</h4>
                    <Badge variant={priorityColors[insight.priority]} className="text-xs">
                      {insight.priority}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {insight.description}
                  </p>
                  {insight.action && (
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="h-auto p-0 text-xs text-primary"
                      onClick={() => navigate(insight.action!.path)}
                    >
                      {insight.action.label} <Zap className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
